/**
 * Animation Utilities
 * Shared animation functions for consistent UI effects across components
 *
 * @module animation
 */

/**
 * Default animation configuration
 * Used across MessageList and BlockStreamBuffer
 */
export const FADE_IN_DEFAULTS = {
  duration: 200,
  easing: 'ease-out',
  slideDistance: 6
};

/**
 * Apply fade-in animation to an element
 * Uses double requestAnimationFrame for reliable animation triggering
 *
 * @param {HTMLElement} element - Element to animate
 * @param {Object} options - Animation options
 * @param {number} [options.duration=200] - Animation duration in ms
 * @param {string} [options.easing='ease-out'] - CSS easing function
 * @param {number} [options.slideDistance=6] - Vertical slide distance in px
 * @param {number} [options.delay=0] - Delay before animation starts in ms
 *
 * @example
 * // Basic usage with defaults
 * fadeIn(element);
 *
 * @example
 * // Custom options
 * fadeIn(element, { duration: 300, slideDistance: 8 });
 *
 * @example
 * // Staggered animation with delay
 * fadeIn(element, { delay: 500 });
 */
export function fadeIn(element, options = {}) {
  const {
    duration = FADE_IN_DEFAULTS.duration,
    easing = FADE_IN_DEFAULTS.easing,
    slideDistance = FADE_IN_DEFAULTS.slideDistance,
    delay = 0
  } = options;

  // Set initial state (invisible, slightly below final position)
  element.style.opacity = '0';
  element.style.transform = `translateY(${slideDistance}px)`;

  // Build transition with optional delay
  const transitionValue = delay > 0
    ? `opacity ${duration}ms ${easing} ${delay}ms, transform ${duration}ms ${easing} ${delay}ms`
    : `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`;
  element.style.transition = transitionValue;

  // Trigger animation on next frame
  // Double rAF ensures styles are applied before transition starts
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';

      // Clear inline styles after animation so they don't override
      // hover/other transforms defined in stylesheets.
      const onDone = (e) => {
        if (e.target !== element || e.propertyName !== 'transform') return;
        element.removeEventListener('transitionend', onDone);
        clearFadeInStyles(element);
      };
      element.addEventListener('transitionend', onDone);
    });
  });
}

/**
 * Remove fade-in animation styles from element
 * Call this after animation completes if you need to clean up inline styles
 *
 * @param {HTMLElement} element - Element to clean
 */
export function clearFadeInStyles(element) {
  element.style.opacity = '';
  element.style.transform = '';
  element.style.transition = '';
}
